/** @module Apparel/SamplesForm */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/promise/all",
	"dojo/store/Memory",
	"csi",
	"csi/api",
	"csi/_base/underscore",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/def",
	"csi/def/Action",
	"csi/def/Field",
	"csi/def/Relationship",
	"csi/def/ViewRef",
	"csi/dgrid/MatrixEditor",
	"csi/form/Widget",
	"csi/html",
	"csi/locale",
	"csi/message",
	"csi/reflection",
	"csi/service/nodeService",
	"csi/state/rootStore",
	"csi/sessionConstants",
	"csi/store/_base",
	"csi/store/Names",
	"csi/xml",
	"csi/xml/Query",
	"Apparel/model/productSource",
	"./ProductType",
	"Sourcing/SampleRightWidget",
	"Sourcing"
], function(declare, lang, all, Memory, csi, api, _, comm, PostRequest, def, Action, Field, Relationship, ViewRef, MatrixEditor, Widget,
	html, locale, message, reflection, {resolveNode, resolveNodes}, {routerStore}, sessionConstants, csiStore, Names, xml, Query, {ATTR_SOURCE_SAMPLES, ATTR_SOURCING_SOURCES, TYPE_PRODUCT_SOURCE}, ProductType, SampleRightWidget, Sourcing) {

	// SamplesForm is used from many places:
	//	Product (Style,Material,Shape) Samples view
	//	ProductSource Samples view
	//	SupplierRequest view of Samples
	//	Size Chart Review Evaluate form

	api.addFormFieldPersistException("Sample", '__Form__', 'SampleSRLineItem', true);

	var _SetupClass = declare(null, {
		runRequest: true,

		request: function(/*Widget*/form, /*Controller*/ac, /*String[]*/urls, /*String[]*/paths) {
			// summary: add urls and paths needed for product
			if (form.urlProduct) {
				urls.push(form.urlProduct);
				paths.push("Child:ProductColors;ProductSizes;Attributes");
				paths.push("Child:ShapeSizeRange/Child:Sizes");
			}
		},

		init: function(/*Widget*/form, /*Controller*/ac) {
			// summary: initialize colors, sizes, and defaults based on Product
			var cache = form.cacheView;
			var product = cache.getNode(form.urlProduct);
			var colors = [];
			var sizes = [];
			var defaultSize = null;
			if (product) {
				if (product.$Type == "Shape") {
					var sizeRange = cache.getNode(product.ShapeSizeRange);
					if (sizeRange) {
						sizes = sizeRange.Sizes;
						defaultSize = sizeRange.BaseSize;
					}
				} else {
					colors = cache.getNodes(product.ProductColors).filter(n => n.Active);
					sizes = product.ProductSizes;
					defaultSize = product.DefaultSize;
				}
			}
			var dimValues = form._setProductDimensions(colors, sizes, defaultSize, true);

			// if product type does not support size range, then hide field; only have to worry about StyleType since it is the only one with a Size Range
			// however, never hide size range if product is a Shape
			if (product && form.defView.getField(form.SIZE_RANGE) && (product.$Type == "Material" || (product.$Type == "Style" && !ProductType.hasSize(product)))) {
				form.defView.getField(form.SIZE_RANGE).bHidden = true;
			}

			// specify default values if not already specified
			form.addDefaultValue(xml.ATTR_NAME, ac.m_formData.getFieldValue(xml.ATTR_NAME));

			// decide default value for dimensions
			var dimDefault = form.DIM_NONE;
			if (form._urlBaseSize)
				dimDefault = form.DIM_BASE_SIZE;
			if (dimValues && dimValues[0] == form.DIM_COLORS) // could happen if on a colored material
				dimDefault = form.DIM_COLORS;

			form.addDefaultValue(form.DIMENSIONS, dimDefault);
			form.addDefaultValue("ResponsibleUsers", [sessionConstants.userUrl]);

			return dimValues;
		}
	});

	return declare(Widget, {
		'class': 'csi-form-view-block',

		// field ids
		COLOR: "ProductColor",
		SIZE: "ProductSize",
		COLORS: "Colors",
		SIZES: "Sizes",
		DIMENSIONS: "RequestSampleDimensions",
		LINE_ITEM: "SampleSRLineItem",
		QUANTITY: "RequestSampleQuantity",
		SAMPLE_STORAGE: "SampleStorage",
		SIZE_RANGE: "RequestSampleSizeRange",
		SUPPLIER: "Supplier",
		FACTORY: "Factory",
		SUPPLIER_FACTORY: "SupplierFactory",
		CUSTOM_QUANTITY: "RequestSampleCustomQuantity",
		COPY_SAMPLE_BOM: "CopySampleBOM",

		// dimension choices
		DIM_NONE: "0",
		DIM_BASE_SIZE: "1",
		DIM_COLORS: "2",
		DIM_SIZES: "3",
		DIM_COLORS_AND_SIZES: "4",
		DIM_PER_ACTIVE_COLOR: "5",

		/** @property {boolean}: true if creating SupplierItem under SupplierRequest - populated on NewSupplierRequestSamples onCreatePopupParams */
		bSupplierRequest: false,

		// Boolean: true on redraws to let us re-load the whole form
		bRedraw: false,

		// String: URL to Product for which samples are being created
		urlProduct: null,

		// String: URL to SupplierRequest and optional line item for which samples are being created
		urlRequest: null,

		/** @property {string} urlSource - URL to Source for which samples are being created */
		urlSource: null,

		/** @property {string} sourceName - Name to source for which samples are being created */
		sourceName: null,

		// String[]: Colors and Sizes displayed for selection - sizes displayed can change based on alternate size range
		_colors: null,
		_sizes: null,

		// String: URL to Color if sample is tied to a colored material
		productColor: null,

		// String: color and size field names (NOT labels)
		colorsLabel: null,
		sizesLabel: null,

		// String[]: list of all available sizes
		_defaultBaseSize: null,
		_defaultSizes: null,

		// Field[]: dimension fields
		_dimensionFields: null,

		// Object[]: dimension items
		_dimensionStore: null,

		// MatrixEditor: custom quantity matrix editor
		_matrix: null,

		//Flag to determine if style ColorAllocationStatus = WIP
		isAllocationWIP: null,

		/** @property {DOMNode} factoriesNode - node containing # of factories selected */
		factoriesNode: null,

		/** @property {DOMNode} suppliersNode - node containing # of suppliers selected */
		suppliersNode: null,


		// Variable containing url of suppliers selected */
		urlSupplierSelected: null,

		// DOMNode: row containing custom quantity matrix
		_matrixRow: null,

		// Map<Product, ProductColor[]>: active product colors - keys and values are URL's
		_productColors: null,

		// String|Function: class to use to initialize form
		_productSetupClass: _SetupClass,

		// Object: instance of _productSetupClass
		_productSetup: null,

		// String: type of Sample to create based on product type
		_typeSample: null,

		// String: currently selected base size
		_urlBaseSize: null,

		// Int: keep track of how many Samples were created for batch purpose
		batchSampleCtr: null,

		parentNode: null,

		getFieldStore: function(/*Field*/field, /*String*/value) {
			// summary: if field is for LineItem or Supplier, then add SelectAll option
			var name = field.getControlName();
			if (name == this.DIMENSIONS) {
				return this._dimensionStore;
			}

			if (name == this.LINE_ITEM && this.urlRequest) {
				return this._getLineItemFieldStore(value);
			} else if (this.urlProduct && name == this.sizesLabel) {
				return this._getSizeFieldStore(value) || this.inherited(arguments);
			}

			return this.inherited(arguments);
		},

		/**
		 * Handle retrieving data for lineitem field stores
		 * @param {String} value - initial value for the field
		 */
		_getLineItemFieldStore: function(value) {
			var gotoDialog = "SampleSRLineItem";
			var params = {
				m_id: "LineItems",
				m_url: this.urlRequest,
				m_query: new Query().RefAttr("SRLineItemProduct", xml.OP_NOT_EQUALS, csi.EMPTY_URL)
			};

			return this._getStore(gotoDialog, params, value);
		},

		/**
		 * Handle retrieving data for Color field/Matrix stores
		 * @param {String} value - initial value for the field
		 */
		_getColorFieldStore: function(value) {
			var gotoDialog = "ProductColor";
			var params = {m_id: "ProductColors"};
			var q = new Query();
			let isInProductSource = this.getSourceController().getAction().classController === "Apparel.NewProductSourceSamples";
			let product = this.cacheView.getNode(this.urlProduct);
			if (isInProductSource) {
				this.isAllocationWIP = Sourcing.isProductColorwayAllocationWIP(product);
			}
			if (this.bSupplierRequest) {
				const lineItem = this.cacheView.getNode(this.getFieldValue(this.LINE_ITEM)[0]);
				product = this.cacheView.getNode(lineItem.SRLineItemProduct);
				this.isAllocationWIP = Sourcing.isProductColorwayAllocationWIP(product);
			}
			if ((this.isAllocationWIP && this.urlSupplierSelected) || (this.isAllocationWIP && isInProductSource)) {
				let source = isInProductSource ? this.urlSource : this.urlSupplierSelected;
				q.RefAttr(this.isSupplierFactory() ? "SupplierFactory" : "Supplier", xml.OP_EQUALS, source, "Child:ColorwayColorSources/Child:__Parent__")
					.Attr("Active", xml.OP_EQUALS, true, "Child:ColorwayColorSources");
				let activeColorsInActiveColorProductSources;
				if (!isInProductSource) {
					activeColorsInActiveColorProductSources = Sourcing.getActiveColorsInActiveColorProductSources(product, source, this.cacheView);
					
				} else {
					const colorSourceNodes = this.cacheView.getNodes(this.getSourceContext().getNode().ColorSources);
					activeColorsInActiveColorProductSources = colorSourceNodes.filter((cs) => cs.Active).map((cs) => cs.Colorway);
				}
				if (!activeColorsInActiveColorProductSources.length) {
					q.Node(xml.PROP_URL, xml.OP_EQUALS, activeColorsInActiveColorProductSources);
				}
				let qOr = q.Or();
				activeColorsInActiveColorProductSources.forEach((c) => {
					qOr.Node(xml.PROP_URL, xml.OP_EQUALS, c);
				});
			} else {
				q.Attr("Active", xml.OP_EQUALS, true);
			}
			params.m_url = this.urlProduct;
			params.m_query = q;

			return this._getStore(gotoDialog, params, value);
		},

		/**
		 * Handle retrieving data for Size field/Matrix stores
		 * @param {String} value - initial value for the field
		 */
		_getSizeFieldStore: function(value) {
			const srField = this.defView.getField(this.SIZE_RANGE);

			var gotoDialog = "ProductSize";
			var params;
			if (srField && xml.isValidURL(srField.getValue())) {
				params = {
					m_id: "Sizes",
					m_url: srField.getValue()
				};
			} else if (xml.isValidURL(this.urlSizeOwner)) {
				const sizeOwnerType = this.cacheView.getNode(this.urlSizeOwner).$Type;
				params = {
					m_id: sizeOwnerType == "SizeChartRevision" ? "SelectedSizes" : "Sizes",
					m_url: this.urlSizeOwner
				};
			} else {
				return;
			}

			return this._getStore(gotoDialog, params, value);
		},

		/**
		 * Handle retrieving of store and its action
		 * @param {String} gotoDialog - id of gotoDialog
		 * @param {String} params - different field params
		 * @param {String} value - initial value for the field
		 */
		_getStore: function(gotoDialog, params, value) {
			if (params) {
				var rel = lang.mixin(new Relationship(), params);
				var storeOptions = csiStore.namesOptionsByRelationship(this.getContext(), rel);
				storeOptions.action = lang.mixin(new Action(), {
					m_name: locale.resource("AggregateInline_Name", [locale.typePlural(gotoDialog)]),
					m_viewRef: lang.mixin(new ViewRef(), params),
					m_fixedQuery: params.m_query // possible extra filter
				});

				// make sure current value(s) are in store so it does not have to be queried
				var store = new Names(storeOptions);
				var node;

				if (value != null && (node = this.cacheView.getNode(value, true))) {
					var extraItem = csiStore.nameObject(node);
					store.put(extraItem);
				}

				return store;
			}
		},

		/**
		 * @override
		 * Set proper parent for client ProductSource node
		 */
		onCreateFormNode: function(node, type) {
			const product = this.cacheView.get(this.urlProduct);
			if (product) {
				if (type === TYPE_PRODUCT_SOURCE) {
					node.__Parent__ = product.ProductSourcing;
				} else if (type === "StyleSourcing") {
					node.__Parent__ = product.$URL;
				}
			}
		},

		/**
		 * @override
		 * Attach form node to parent product
		 */
		onWriteFormReport(field, writer, formInfo) {
			const {nodeUrl, parentUrl, contextUrl} = formInfo;

			// if creating Sample directly under ProductSource, then nothing else to do
			if (parentUrl) {
				return;
			}
			// if creating under product, then create path up to product
			let {cacheView, urlProduct, urlSupplierSelected} = this;
			const product = cacheView.get(urlProduct);

			if (urlSupplierSelected == null && this.bSupplierRequest) {
				urlSupplierSelected = this.getFieldValue(this.isSupplierFactory() ? this.FACTORY : this.SUPPLIER)[0];
			}

			if (product) {
				this.createNewOrUseExistingProductSource(product, writer, cacheView, nodeUrl, urlSupplierSelected);
			}
			
			if (this.getSourceContext().getNodeType() === "ColorMaterial") {
				writer.changeNode(urlProduct)
					.publishAttribute("ContextNode", xml.ATTR_REF, contextUrl);
			}
		},

		/**
		 * @override
		 * Only fill in active colorways during copy action
		 */
		getValueForField: function(field, node) {
			const {cacheView, COLOR, isAllocationWIP} = this;
			if (this.isCopy() && field.m_id === COLOR) {
				const sampleColor = this.getViewNode().ProductColor;
				const activeSampleColor = cacheView.getNode(sampleColor)?.Active ? sampleColor : null;

				// Only fill in active color product sources if color allocation is enabled
				if (isAllocationWIP) {
					return cacheView.getPathNodes(this.getViewNode(), "Child:__Parent__/Child:ColorSources")
						.filter(cps => cps.Active && activeSampleColor === cps.Colorway)
						.map(cps => cps.Colorway);
				}

				return activeSampleColor;
			}
			return this.inherited(arguments);
		},

		/**
		 * Check if productSource exists for Supplier/Factory, if not then create a new one and then use that when creating sample
		 */
		createNewOrUseExistingProductSource(product, writer, cacheView, nodeUrl, urlSupplierSelected) {
			const productSourcing = cacheView.get(product.ProductSourcing);
			const productSourcesUrls = productSourcing?.ProductSources;
			let hasProductSourceSuppliers = false;
			let productSourceCNL;
			const isSupplierFactory = this.isSupplierFactory();

			productSourcesUrls?.forEach(function(a) {
				const nodeProductSource = cacheView.get(a);
				if (nodeProductSource) {
					const source = isSupplierFactory ? nodeProductSource?.SupplierFactory : nodeProductSource?.Supplier;
					if (urlSupplierSelected === source) {
						productSourceCNL = nodeProductSource.$URL;
						hasProductSourceSuppliers = true;
					}
				}
			});
			//if the supplier have already attached a product source, We don't need to create a new product source
			//, we should use the existing one
			if (hasProductSourceSuppliers) {
				writer.changeNode(nodeUrl).changeAttribute(xml.ATTR_PARENT, xml.ATTR_REF, productSourceCNL);
			} else {
				writer.createNode(null, TYPE_PRODUCT_SOURCE)
					.attach(product.ProductSourcing, ATTR_SOURCING_SOURCES)
					.changeAttribute(ATTR_SOURCE_SAMPLES, xml.ATTR_REFLIST, [nodeUrl]);
			}
		},

		setColor: function(/*Node*/color) {
			// summary: set color for form
			this.cacheView.cacheNode(color);
			this.addFixedValue(this.COLORS, [color.$URL]);
			this.productColor = color.$URL;
		},

		setProduct: function(/*Node*/product, /*String|Function?*/setupClass) {
			if (!product) {
				return;  // nothing to do
			}

			// summary: set product for form indicating the type of sample to create and possible colors and sizes
			this.cacheView.cacheNode(product);
			this.urlProduct = product.$URL;
			this._setProductType(product.$Type);

			// update form node with new parent product
			const formNode = this.getFormNode();
			const productSource = this.cacheView.get(formNode.__Parent__);
			this.onCreateFormNode(productSource, TYPE_PRODUCT_SOURCE);

			var colorsField = this.defView.getField(this.colorsLabel);
			var sizesField = this.defView.getField(this.sizesLabel);

			// if product type doesn't support colors or sizes, take off the fields
			if (colorsField && !ProductType.hasColor(product))
				colorsField.bHidden = true;

			if (sizesField && product.$Type != "Shape" && !ProductType.hasSize(product)) // shapes always have sizes
				sizesField.bHidden = true;

			if (colorsField && colorsField.m_control) { // make sure field exists
				this.setFieldValue(colorsField, []);
				colorsField.m_control.set("store", this.getFieldStore(colorsField));
			}

			if (sizesField && sizesField.m_control) { // make sure field exists
				this.setFieldValue(sizesField, []);
				sizesField.m_control.set("store", this.getFieldStore(sizesField));
			}

			if (setupClass) {
				this._productSetupClass = setupClass;
			}
		},

		/**
		 * @deprecated use setSource
		 */
		setSupplier: function(urlSource, sourceName) {
			this.setSource(urlSource, sourceName)
		},

		/** Set Source URL and Name for which Samples will be created */
		setSource(urlSource, sourceName) {
			this.urlSource = urlSource;
			this.sourceName = sourceName;
			this.defView.removeField(this.SUPPLIER);
			this.defView.removeField(this.FACTORY);
			if (this.isAllocationWIP) {
				this.urlSupplierSelected = urlSource;
				this.resetFieldStoreAndValue(this.COLORS);
			}
		},

		//set the initial value to the Sample
		setSampleName: function(sizeChartReviewName, fd) {
			fd.setFieldInitialValue(xml.ATTR_NAME, sizeChartReviewName);
		},

		setSupplierRequest: function(/*Node*/sr) {
			// summary: set SupplierRequest for which sample is being created
			var cache = this.cacheView;
			var lineItem, product;

			if (sr) {
				cache.cacheNode(sr);
				this.urlRequest = sr.$URL;
				this._setProductType(xml.enumGetText(sr.RequestType));
				const field = this.defView.getField(this.SUPPLIER) || this.defView.getField(this.FACTORY);

				// init Supplier field
				var fSupplier = field;
				if (fSupplier) {
					fSupplier.m_bRequired = true;
					fSupplier.m_relationship = lang.mixin(new Relationship(), {
						m_url: this.urlRequest,
						m_id: this.isSupplierFactory() ? "SRSupplierFactories" : "SRSources/Child:Supplier",
						m_query: this.isSupplierFactory() ? null : new Query().Attr("AutoCreateProductSourceOnSR", xml.OP_EQUALS, true).SortByNode(xml.PROP_NAME) // only Suppliers with AutoCreateProductSourceOnSR is set to true
					});

				}

				var fProductOnSr = this.defView.getField(this.LINE_ITEM);
				if (this.urlProduct) {
					// if product already specified, then find line item in request that points to product;
					// assumption is that product is guaranteed to already be in request
					_.some(sr.LineItems, function(url) {
						lineItem = cache.getNode(url, true);

						if (lineItem.SRLineItemProduct == this.urlProduct) {
							return true;
						}

						return false;
					}, this);
				} else if (sr.RequestType == "DataPackageContainerType:Style") {
					// init style field to select line item - pre-select if only one
					fProductOnSr.m_bRequired = true;
					fProductOnSr.m_relationship = lang.mixin(new Relationship(), {
						m_url: this.urlRequest,
						m_id: "LineItems"
					});

				} else {
					// init material field to select line item - pre-select if only one
					// query to remove any hidden materials from the dropdown
					var rel = lang.mixin(new Relationship(), {
						m_url: this.urlRequest,
						m_id: "LineItems",
						m_query: new Query().Attr("Hidden", xml.OP_EQUALS, false, "Child:SRLineItemProduct")
					});

					fProductOnSr.m_bRequired = true;
					fProductOnSr.m_relationship = rel;

					var unhiddenCount = 0;
					var lineItems = this.cacheView.getChildren(sr, "LineItems", true);

					_.each(lineItems, function(l) {
						var lineItemProduct = this.cacheView.getNode(l.SRLineItemProduct, true);
						if (lineItemProduct && !lineItemProduct.Hidden) {
							unhiddenCount++;
							lineItem = l;
						}
					}, this);

					// if there is only SRLineItem with an assigned product, preselect that item in the dialog
					if (lineItem && unhiddenCount == 1) {
						product = cache.getNode(lineItem.SRLineItemProduct, true);
						this.urlProduct = product.$URL;
						this.addFixedValue(this.LINE_ITEM, lineItem.$URL);
					}
				}
			} else {
				// reset members
				this.urlRequest = null;
				this.addFixedValue(this.LINE_ITEM, null);
			}
		},

		toWriterSamples: function(/*Controller*/ac) {
			// summary: write samples for each product/supplier combination - if for SupplierRequest, then also make
			// sure to connect to SRLineItem
			var fd = this.defView.m_formData;
			var urlLineItem = fd.getFieldValue(this.LINE_ITEM, true);
			const url = fd.getFieldValue(this.SUPPLIER, true) || fd.getFieldValue(this.FACTORY, true)
			let urlSource = url || this.urlSource;
			var samples = [];
			this.batchSampleCtr = 0;

			var sr = this.cacheView.getNode(this.urlRequest);
			if (sr || (urlSource?.length > 0)) {
				// iterate through all sources and line items
				var sources = xml.isValidURL(urlSource) ? [urlSource] : sr.SRSuppliers;

				if (sources?.length > 0 && (sources[0] instanceof Array)) {
					sources = sources[0];
				}

				if (urlLineItem != null) {
					_.each(urlLineItem, function(l) {
						var lineItem = this.cacheView.getNode(l, true);
						var lineItemProduct = this.cacheView.getNode(lineItem.SRLineItemProduct, true);

						if (sr.RequestType == "DataPackageContainerType:Style" || !lineItemProduct.Hidden) {
							_.each(sources, function(s) {
								samples = samples.concat(this._toWriterProductSamples(ac, lineItem.SRLineItemProduct, s, lineItem.$URL));
							}, this);
						}
					}, this);
				} else {
					_.each(sources, function(s) {
						samples = samples.concat(this._toWriterProductSamples(ac, this.urlProduct, s));
					}, this);
				}
			} else {
				// create samples for single product and source
				samples = samples.concat(this._toWriterProductSamples(ac, this.urlProduct, urlSource));
			}

			return samples;
		},

		_buildForm: function() {
			this.inherited(arguments);
			this._updateStatistics();
		},

		_buildFields: function(/*Field[]*/fields) {
			// summary: call setup instance to update form fields and defaults based on context
			this._preProcessProduct();

			this.inherited(arguments);

			// SRLineItem field is not needed if not in context of SupplierRequest
			this.setFieldHidden(this.LINE_ITEM, !this.urlRequest);

			this._postProcessProduct();
		},

		_getFieldRelationship: function(/*Field*/f, /*String*/urlNode, /*String?*/value) {
			// summary: return relationship for field
			if (f.getControlName() == this.SIZE_RANGE) {
				// return query for all active size ranges
				var query = new Query();
				query.Or()
					.Attr("OKforStyle", xml.OP_EQUALS, true)
					.Attr("OKforSizeChart", xml.OP_EQUALS, true);

				return lang.mixin(new Relationship(), {
					m_typeNode: "SizeRange",
					m_query: query,
					m_depPaths: ["Child:Sizes"]
				});
			}

			return this.inherited(arguments);
		},

		_getProductSetup: function() {
			// summary: if product is known, then return instance of product setup class
			if (!this.urlProduct && this._typeSample == "MaterialSample") return null;
			if (!this._productSetup) {
				var ctr = csi.syncRequire(this._productSetupClass);
				this._productSetup = new ctr();
			}
			return this._productSetup;
		},

		_getRootRequest: function(/*Node*/node) {
			// summary: get root request for nodes needed by view - forward call to setup instance
			// as the request will differ depending on context of form
			var req = this.inherited(arguments);
			var ps = this._getProductSetup();
			var urls = [];
			var paths = [];

			if (ps && ps.runRequest) {
				ps.request(this, this.defView.getSource(), urls, paths);
			}

			if (this.urlRoot) {
				urls.push(this.urlRoot);
				paths.push("Child:SampleDataSheets");
			}

			if (urls.length) {
				req = req || new PostRequest(comm.MOD_SEARCH, comm.OP_URL);
				req.addParameter(comm.QUERY_URL, urls, true);
				req.addDependencyPaths(paths);
			}

			return req;
		},

		onChangeField(field) {
			this.inherited(arguments);
			this._updateStatistics();
		},

		_onChangeDimensions: function(defView, field, value) {
			// summary: render custom input checkbox based on selection
			var customQtyField = this.getField(this.CUSTOM_QUANTITY);
			var bCustomQty = customQtyField.getValue();
			var bCanCustomizeQty = value > 1 && value != this.DIM_PER_ACTIVE_COLOR;

			this.setFieldHidden(customQtyField, !bCanCustomizeQty);

			if (this.productColor) {
				this.setFieldDisabled(customQtyField, true);
			} 

			if (bCustomQty && !bCanCustomizeQty) {
				this.setFieldValue(customQtyField, false);
			} else {
				this._onChangeCustomQuantity(defView, customQtyField, bCustomQty);
			}
		},

		_onChangeCustomQuantity: function(defView, field, value) {
			// summary: render size / color fields based on selection
			//  0/1 - remove size/color
			//  2 - color
			//  3 - size
			//  4 - size & color
			// also when custom input is enabled removed size/color/quantity fields

			var dimensionsFieldValue = this.defView.getFieldValue(this.DIMENSIONS);

			this.setFieldHidden(this.colorsLabel, value || (dimensionsFieldValue != this.DIM_COLORS && dimensionsFieldValue != this.DIM_COLORS_AND_SIZES));
			this.setFieldHidden(this.sizesLabel, value || (dimensionsFieldValue != this.DIM_SIZES && dimensionsFieldValue != this.DIM_COLORS_AND_SIZES));
			this.setFieldHidden(this.QUANTITY, value);

			this._removeMatrix();

			if (value) {
				this._addMatrix();
			}

			this.publishDomChange();
		},

		_addMatrix: function() {
			// no matrix needed for first two values
			var value = this.defView.getFieldValue(this.DIMENSIONS);
			if (value < this.DIM_COLORS) {
				return;
			}

			var bColumns = (value == this.DIM_COLORS_AND_SIZES);
			var bColors = bColumns || (value == this.DIM_COLORS);

			this._matrixRow = this._createFieldDom();

			// form using csi-form-view-block is using display:block, so no need to create th or td
			var domNode = html.create('div', {style: 'padding-top: 6px;'}, this._matrixRow);

			var colorFieldStore = this._getColorFieldStore(this.productColor);
			const sizeField = this.defView.getField(this.sizesLabel);
			var sizeFieldStore = (sizeField != null) ? this.getFieldStore(sizeField) : null;

			//Create Memory store from field store query results
			function _createMatrixStoreFromFieldDataStore(results, store) {
				var idProperty = store.idProperty;
				var labelProperty = store.labelProperty;
				return new Memory({
					labelProperty: "name",
					data: csi.transform(results, function(item) {
						var id = item[idProperty];
						return (xml.isValidURL(id)) ? {id: id, name: item[labelProperty]} : null;
					})
				});
			}

			//Declare deferred arrays.
			var dfds = [
				bColors ? colorFieldStore.query() : sizeFieldStore.internalQuery()
			];

			if (bColumns) {
				dfds.push(sizeFieldStore.internalQuery());
			}

			this.when(all(dfds), results => {
				// it's possible that while request was pending the matrix was deleted and recreated, so ensure element still in DOM
				if (html.inDOM(domNode)) {
					// matrix should always default to 0 and not be tied to RequestSampleQuantity field
					this._matrix = new MatrixEditor({
						defaultValue: 0,
						label: locale.resource(this.QUANTITY),
						editorArgs: {constraints: {min: 0, max: api.getSamplesQuantityMax()}},
						columnLabel: bColumns ? locale.resource(this.sizesLabel) : '',
						columnStore: results[1] ? _createMatrixStoreFromFieldDataStore(results[1], sizeFieldStore) : null,
						rowLabel: locale.resource(bColors ? this.colorsLabel : this.sizesLabel),
						rowStore: _createMatrixStoreFromFieldDataStore(results[0], bColors ? colorFieldStore : sizeFieldStore),
						onChange: (value) => this._updateStatistics()
					}).placeAt(domNode);
					this._updateStatistics();
				}
			});
		},

		_removeMatrix: function() {
			html.destroyElement(this._matrixRow);
			this._matrixRow = null;
			this._matrix = null;
			this._updateStatistics();
		},

		_updateStatistics: async function() {
			const {cacheView, rightWidget, defView, _matrix, SIZES, COLORS} = this;

			if (!rightWidget)
				return; // we're here too early

			let colorSizePairsLength = 0;
			let colorsLength = 0;
			let sizesLength = 0;
			let products = [];
			let sourcesLength = 1;
			const qty = _matrix ? 1 : this.getFieldValue(this.QUANTITY)
			let numSamples = 0;

			const supplierField = this.getField(this.SUPPLIER);
			const factoryField = this.getField(this.FACTORY);
			const supplierValue = this.getFieldValue(this.SUPPLIER);
			const factoryValue = this.getFieldValue(this.FACTORY);

			if (supplierField && supplierValue.length != 0) {
				sourcesLength = supplierField.m_typeControl === def.FIELD_MULTI_SELECT ? supplierValue.length : (supplierField === "" ? 0 : 1);
			} else if (factoryField && factoryValue.length != 0) {
				sourcesLength = factoryField.m_typeControl === def.FIELD_MULTI_SELECT ? factoryValue.length : (factoryField === "" ? 0 : 1);
			} else if (this.actionOnSizeChart()) {
				sourcesLength = 1;
			} else {
				sourcesLength = 0;
			}

			const parentNode = defView.getSource().getParent();
			if (parentNode.$Type == "StyleSourcing" || parentNode.$Type == "MaterialSourcing") {
				products = [parentNode.__Parent__];
			} else if (parentNode.$Type == "Style" || parentNode.$Type == "Material") {
				products = [parentNode.$URL];
			} else if (parentNode.$Type == TYPE_PRODUCT_SOURCE) {
				products = [cacheView.getNode(parentNode.__Parent__).__Parent__];
				sourcesLength = 1;
			} else {
				const lineItems = this.getFieldValue(this.LINE_ITEM);
				if (Array.isArray(lineItems)) {
					for (const li of lineItems) {
						products.push(cacheView.getNode(li).SRLineItemProduct);
					}
				}
			}

			const value = this.getFieldValue(this.DIMENSIONS);
			if (value == this.DIM_NONE) {
				colorSizePairsLength = products.length;
			} else if (value == this.DIM_BASE_SIZE) {
				sizesLength = 1;
				colorSizePairsLength = products.length;
			} else if (value == this.DIM_PER_ACTIVE_COLOR) {
				// loop through all the line items, get the Products and count the active colors
				for (const product of products) {
					const productNode = cacheView.getNode(product);
					this.isAllocationWIP = Sourcing.isProductColorwayAllocationWIP(productNode);
					let supplierOrFactoryValue = this.isSupplierFactory() ? factoryValue : supplierValue;
					if (this.isAllocationWIP) {
						if (this.bSupplierRequest) {
							for (let i = 0; i < this.getFieldValue(this.LINE_ITEM).length; i++) {
								const lineItem = this.getFieldValue(this.LINE_ITEM)[i]
								// if Product has Allocation WIP, only want to count colors that are in active colorProductSources using the lineItem
								let allocatedCPSColorsLength = Sourcing.getCountColorsInActiveCPS(productNode, supplierOrFactoryValue, cacheView, lineItem);
								colorSizePairsLength += allocatedCPSColorsLength;
								// numSamples just count for colors in allocated CPS
								numSamples += allocatedCPSColorsLength * qty;
							}
						} else {
							// if Product has Allocation WIP, only want to count colors that are in active colorProductSources without using lineItem if outside of SR
							let allocatedCPSColorsLength = Sourcing.getCountColorsInActiveCPSNoLineItem(productNode, this.urlSource === null ? supplierOrFactoryValue : this.urlSource, cacheView);
							colorSizePairsLength += allocatedCPSColorsLength;
							// numSamples just count for colors in allocated CPS
							numSamples += allocatedCPSColorsLength * qty;
						}
					} else {
						let pcLength;
						if (productNode.$Type == "Style") {
							pcLength = productNode.ActiveColorways.length;
						} else {
							const productColors = await resolveNodes(productNode.ProductColors, cacheView);
							pcLength = productColors.filter(n => n.Active).length;  // have to filter manually
						}
						colorSizePairsLength += pcLength;
						// if product is not Allocation WIP, we just count numSamples as num of Active colors * sourcesLength * qty
						numSamples += pcLength * sourcesLength * qty;
					}

				}
				if (products.length > 1) {
					colorsLength = "--";
				} else {
					colorsLength = colorSizePairsLength;
				}
			} else if (value == this.DIM_SIZES) {
				if (_matrix) {
					sizesLength = colorSizePairsLength = 0;
					const matrixValues = _matrix.get("value");
					_matrix.rowStore.query().forEach((item, i) => {
						const values = matrixValues[i];
						if (values[0]) {
							sizesLength++;
							colorSizePairsLength += values[0];
						}
					});
				} else {
					sizesLength = colorSizePairsLength = this.getFieldValue(SIZES)?.length || 0;
				}
			} else if (value == this.DIM_COLORS) {
				if (_matrix) {
					colorsLength = colorSizePairsLength = 0;
					const matrixValues = _matrix.get("value");
					_matrix.rowStore.query().forEach((item, i) => {
						const values = matrixValues[i];
						if (values[0]) {
							colorsLength++;
							colorSizePairsLength += values[0];
						}
					});
				} else {
					colorsLength = colorSizePairsLength = this.getFieldValue(COLORS)?.length || 0;
				}
			} else {
				// colors & sizes
				if (_matrix) {
					let matrixColors = {}, matrixSizes = {};
					const matrixValues = _matrix.get("value");
					_matrix.rowStore.query().forEach((item, i) => {
						const values = matrixValues[i];
						const sizeResults = _matrix.columnStore.query();
						sizeResults.forEach(function(size, j) {
							if (values[j]) {
								matrixColors[i] = true;
								matrixSizes[size.id] = true;
							}
						});
					});

					sizesLength = Object.keys(matrixSizes).length;
					colorsLength = Object.keys(matrixColors).length;
					colorSizePairsLength = this.getCountSelectedColorSizePairs();
				} else {
					sizesLength = this.getFieldValue(SIZES)?.length || 0;
					colorsLength = this.getFieldValue(COLORS)?.length || 0;
					colorSizePairsLength = sizesLength * colorsLength;
				}
			}


			rightWidget.set("numSuppliers", sourcesLength);
			rightWidget.set("numFactories", sourcesLength);
			rightWidget.set("numColorways", colorsLength);
			rightWidget.set("numColoredMaterials", colorsLength);
			rightWidget.set("numSizes", sizesLength);
			if (value == this.DIM_PER_ACTIVE_COLOR) {
				// when samples per active color is selected, numSamples need to be used to account for multiple products which might contain
				// products which are both Allocated WIP or not Allocated WIP
				rightWidget.set("numSamples", numSamples);
			} else {
				rightWidget.set("numSamples", colorSizePairsLength * sourcesLength * qty);
			}
		},

		/**
		 * Get number of selected color / size pairs
		 */
		getCountSelectedColorSizePairs() {
			const dims = this.getFieldValue(this.DIMENSIONS);
			const colors = this.getFieldValue("Colors");
			const sizes = this.getFieldValue("ProductSizes");
			const matrix = this._matrix;
			const matrixValues = matrix ? matrix.get("value") : null;
			let ct = 0;

			if (dims == this.DIM_COLORS) {
				if (matrix) {
					matrix.rowStore.query().forEach((item, i) => {
						const values = matrixValues[i];
						if (values[0])
							ct += values[0];
					});
				} else {
					ct += colors.length;
				}
			} else if (dims == this.DIM_SIZES) {
				if (matrix) {
					matrix.rowStore.query().forEach((item, i) => {
						const values = matrixValues[i];
						if (values[0])
							ct += values[0];
					});
				} else {
					ct += sizes.length;
				}
			} else if (dims == this.DIM_COLORS_AND_SIZES) {
				if (matrix) {
					matrix.rowStore.query().forEach((item, i) => {
						const values = matrixValues[i];
						const sizeResults = (dims == this.DIM_COLORS_AND_SIZES) ? matrix.columnStore.query() : null;
						sizeResults.forEach(function(size, i) {
							if (values[i])
								ct += values[i];
						});
					});
				} else {
					ct += colors.length * sizes.length; // cartesian product
				}
			}

			return ct;
		},

		_onChangeLineItem: function(defView, field, value) {
			// summary: update dimensions based on selected product if line item field is visible
			let {bSupplierRequest, FACTORY, SUPPLIER} = this;
			if (!this.isFieldVisible(field)) {
				return;
			}

			// if in SupplierRequest and only one Supplier/Factory is selected, reassign urlSupplierSelected if null
			if (bSupplierRequest && this.urlSupplierSelected === null && this.getFieldValue(this.isSupplierFactory() ? FACTORY : SUPPLIER).length === 1) {
				this.urlSupplierSelected = this.getFieldValue(this.isSupplierFactory() ? FACTORY : SUPPLIER)[0];
			}

			let lineItem = (value.length == 1) ? this.cacheView.getNode(value, true) : null;
			let urlProduct = lineItem ? lineItem.SRLineItemProduct : null;

			if (urlProduct != this.urlProduct) {
				// setup new product
				this._removeMatrix();
				this._resetProductData();
				this.urlProduct = urlProduct;

				if (urlProduct) {
					// issue request to get information for selected product
					message.progress(locale.resource("Action_Message"));

					var req = new PostRequest(comm.MOD_SEARCH, comm.OP_URL);
					var ps = this._getProductSetup();
					var urls = [];
					var paths = [];
					ps.request(this, this.defView.getSource(), urls, paths);
					req.addParameter(comm.QUERY_URL, urls, true);
					req.addDependencyPaths(paths);
					req.go({
						load: lang.hitch(this, this._onLoadProduct)
					});
				} else {
					this._onLoadProduct();
				}
			}
		},

		_onChangeProductSources: function(defView, field, value) {
			let {cacheView, urlProduct, _buildArrayColors, isAllocationWIP} = this;
			if (urlProduct === null && this.bSupplierRequest && this.getFieldValue(this.LINE_ITEM).length === 1) {
				const lineItemNode = cacheView.getNode(this.getFieldValue(this.LINE_ITEM)[0]);
				urlProduct = lineItemNode.SRLineItemProduct;
			}
			isAllocationWIP = Sourcing.isProductColorwayAllocationWIP(cacheView.getNode(urlProduct));
			if (value != csi.EMPTY_URL && urlProduct != null) {
				const fDim = this.defView.getField(this.DIMENSIONS);
				const vDim = Number(fDim.getValue());
				this.urlSupplierSelected = this.bSupplierRequest ? value[0] : value;
				if (isAllocationWIP && this.getSourceContext().getNode().$Type !== "ColorMaterial") {
					this.resetFieldStoreAndValue(this.COLORS);
					//Look into the results the default and other  results to apply
					//Within the Colors/Sizes selection, “Colors”, “Colors and Sizes”, and “Sample per Active Color” should not be available
					const product = cacheView.get(urlProduct);
					const productSourcing = cacheView.get(product.ProductSourcing);
					const productSourcesUrls = productSourcing.ProductSources;
					this.setFieldDisabled(this.DIMENSIONS, false);
					//add this condition when ColorAllocationStatus:Allocation WIP
					const sizes = product.ProductSizes;
					const defaultSize = product.DefaultSize;

					let colors = [];
					const isfactory = this.isSupplierFactory();
					// refresh matrix if Supplier/Factory field changes
					this._onChangeDimensions(defView, fDim, vDim);
					this.resetFieldStoreAndValue(this.DIMENSIONS);
					for (let i = 0; i < productSourcesUrls.length; i++) {
						const nodeProductSource = cacheView.get(productSourcesUrls[i]);
						if (isfactory) {
							const factory = nodeProductSource.SupplierFactory;
							if (value == factory || (this.bSupplierRequest && value.includes(factory))) {
								colors = _buildArrayColors(nodeProductSource, colors, cacheView);
							} else {
								this._restoreDimensionsSelection(fDim, vDim);
							}
						}
						else {
							const supplier = nodeProductSource.Supplier;
							if (value == supplier || (this.bSupplierRequest && value.includes(supplier))) {
								colors = _buildArrayColors(nodeProductSource, colors, cacheView);
							} else {
								this._restoreDimensionsSelection(fDim, vDim);
							}
						}
					}
					if (colors.length == 0) {
						_.each(this._dimensionFields, function(f) {
							if (f.m_name == this.SIZE_RANGE) {
								this.setFieldHidden(f, true);
							} else {
								this.setFieldHidden(f, true);
							}
						}, this);
						this.resetFieldStoreAndValue(this.DIMENSIONS);
					}
					this._setProductDimensions(colors, sizes, defaultSize, true);
					fDim.m_control.set("store", this._dimensionStore);
				} else {
					// only do this when in Supplier Request for the isOneSourceSelected check in _setProductDimensions to be defined after the first time form render
					if (this.bSupplierRequest) {
						this._onLoadProduct();
					}
				}
			}
		},

		_restoreDimensionsSelection: function(fDim, vDim) {
			var values = this._setProductDimensions(
				[],
				this._defaultSizes,
				this._defaultBaseSize,
				true
			);
			if (values.length == 1) {
				// remove dimension matrix if no colors or sizes to select
				vDim = this.DIM_NONE;
			} else {
				fDim.m_control.set("store", this._dimensionStore);
			}
		},

		_buildArrayColors: function(nodeProductSource, colors, cacheView) {
			nodeProductSource.ColorSources.forEach(function(colorURL) {
				let colorSourceNode = cacheView.get(colorURL)
				let colorWayNode = cacheView.get(colorSourceNode.Colorway);
				if (colorWayNode.Active && !colors.map(c => c.$URL).includes(colorWayNode.$URL)) {
					colors.push(colorWayNode);
				}
			});
			return colors;
		},

		_onChangeSizeRange: function(defView, field, value) {
			// summary: reset form controls for dimensions based on new alternate size range
			// if user reset value back to blank, value should be the original size range tied to the product
			if (value == csi.EMPTY_URL) {
				var prodAttributes = this.cacheView.getNode(this.urlProduct).Attributes;
				value = this.cacheView.getNode(prodAttributes).ActualSizeRange;
			}

			var fDim = this.defView.getField(this.DIMENSIONS);
			var vDim = Number(fDim.getValue());
			var sizeRange = this.cacheView.getNode(value, true, function(req) {
				req.addDependencyPaths(["Child:Sizes"]);
			});

			var values = this._setProductDimensions(
				this._colors,
				sizeRange ? sizeRange.Sizes : this._defaultSizes,
				sizeRange ? sizeRange.BaseSize : this._defaultBaseSize,
				true
			);

			if (values.length == 1) {
				// remove dimension matrix if no colors or sizes to select
				vDim = this.DIM_NONE;
			} else {
				fDim.m_control.set("store", this._dimensionStore);

				// if old value is no longer in store, then set to default value
				if (values.indexOf(vDim) < 0) {
					fDim.m_control.set("value", this._urlBaseSize ? this.DIM_BASE_SIZE : this.DIM_NONE);
					return;
				}

				// DE22612 Supplier Request: Mass create samples with alternate size range - initialize form to Base Size
				var selectedBase = -1;
				var selectedNone = -1;
				var sizeRangeItem;

				for (var i = 0; i < fDim.m_control.store.data.length; i++) {
					if (sizeRangeItem = fDim.m_control.store.data[i]) {
						if (sizeRangeItem.id == this.DIM_BASE_SIZE) {
							selectedBase = i;
							break;
						} else if (sizeRangeItem.id == this.DIM_NONE) {
							selectedNone = i;
						}
					}
				}
				var selected = selectedBase >= 0 ? selectedBase : selectedNone;

				if (selected >= 0) {
					fDim.m_control.set("value", selected);
				}
			}

			// update field store for Sizes
			var sizesField = this.defView.getField(this.sizesLabel);

			var params = {
				m_id: "Sizes",
				m_url: value
			};

			var rel = lang.mixin(new Relationship(), params);
			var storeOptions = csiStore.namesOptionsByRelationship(this.getContext(), rel);
			storeOptions.action = lang.mixin(new Action(), {
				m_name: locale.resource("AggregateInline_Name", [locale.typePlural("ProductSize")]),
				m_viewRef: lang.mixin(new ViewRef(), params)
			});

			if (sizesField != null) {
				sizesField.m_control.set("store", new Names(storeOptions)); // update store
				sizesField.m_control.set("value", []); // remove all previous selections
			}
			// update color size matrix
			this._onChangeDimensions(defView, fDim, vDim);
		},

		_onLoadProduct: function(/*Response?*/res) {
			// summary: re-build fields if product selected
			this.cacheView.cacheResponse(res);

			this.setProduct(this.cacheView.getNode(this.urlProduct));

			var dimValues = this._preProcessProduct();
			if (dimValues || !this.urlProduct) {
				this._rebuildDimensionField();
			}

			this._postProcessProduct();

			// hide Colors / Sizes
			var fDim = this.defView.getField(this.DIMENSIONS);
			if (fDim.m_control)
				fDim.m_control.set("value", this.DIM_NONE);

			this.publishDomChange();
			message.hide(true);
		},

		/**
		 * Validate that custom quantities are within range
		 * @param {View} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {Object} params
		 */
		_onValidateCustomQuantity: function(defView, field, value, params) {
			if (this._matrix && !this._matrix.isValid()) {
				throw new Error("SamplesFormCustomQuantityInvalid");
			}
		},

		_preProcessProduct: function() {
			// summary: return array of possible dimension values for given product
			var ps = this._getProductSetup();
			if (!ps) {
				this._setProductDimensions([], [], null, true);
			}
			return ps ? ps.init(this, this.defView.getSource()) : null;
		},

		_postProcessProduct: function() {
			// summary: add additional fields based on product selection
			var fDim = this.defView.getField(this.DIMENSIONS);

			// if there is a product, set dimension fields to visible
			var bHidden = (this.urlProduct == null);

			// alternate size range is always hidden unless we are on a single Style
			var hideSizeRange = true;
			var product = this.cacheView.getNode(this.urlProduct);
			if (!bHidden && product && product.$Type == "Style") {
				hideSizeRange = false;
			}

			_.each(this._dimensionFields, function(f) {
				if (f.m_name == this.SIZE_RANGE) {
					this.setFieldHidden(f, hideSizeRange);
				} else {
					this.setFieldHidden(f, bHidden);
				}
			}, this);

			// render dimension matrix based on current dimension value assuming dimensions is visible
			if (fDim)
				this._onChangeDimensions(this.defView, fDim, fDim.getValue());
		},

		/**
		 * @override
		 * If redrawing, need to refresh all the fields on the form after product is loaded
		 */
		_postView: function() {
			this.inherited(arguments);
			if (this.bRedraw) {
				const field = this.getField(this.LINE_ITEM);
				if (field) {
					this._onChangeLineItem(this.defView, field, this.getFieldValue(this.LINE_ITEM));
				}
				this.bRedraw = false;
			}
		},


		/**
		 * @override
		 * Set redraw flag indicating that we need to refresh the dimensions field.
		 */
		_redraw: function() {
			this.bRedraw = true;
			this.inherited(arguments);
		},

		_rebuildDimensionField: function() {
			var dimField = this.defView.getField(this.DIMENSIONS);

			// dimStore should be ready now - just tie it to the dimField
			if (dimField.m_control)
				dimField.m_control.set("store", this.getFieldStore(dimField));
		},

		_resetProductData: function() {
			// summary: reset product members back to defaults
			this._colors = null;
			this._sizes = null;
			this._defaultBaseSize = null;
			this._defaultSizes = null;
			this._dimensionStore = null;
			this._urlBaseSize = null;
			this.matrixRow = null;
		},

		_resolveView: function() {
			// FIX: fixing this to make SizeChart evaluate form work (in that case, form is in wizard so need different way to access source controller),
			// but this doesn't seem to do the right thing in case of ProductSource samples; urlProduct references the ProductSource rather than Product
			var source = this.isNew() ? null : this.getSourceController();
			let fd = this.defView.m_formData;
			let parentNode = this.parentNode || fd?.getSource().getParent();

			this.isAllocationWIP = Sourcing.isProductColorwayAllocationWIP(parentNode);


			const {FACTORY, SUPPLIER, cacheView} = this;

			if (source) {
				this.urlProduct = source.m_urlParent;
			}

			// Add "Include Quotes / Samples" widget if we are on the New form
			if (this.isNew()) {
				this.rightWidgetClass = SampleRightWidget;
			}

			var sizeField = this.defView.getField(this.SIZE);
			var colorField = this.defView.getField(this.COLOR);

			// fix labels
			if (this.isNew()) {
				if (sizeField) {
					sizeField.m_label = this.SIZES;
					sizeField.m_typeControl = def.FIELD_MULTI_SELECT;
					sizeField.m_bRequired = true;
				}


				if (colorField) {
					colorField.m_label = this.COLORS;
					colorField.m_typeControl = def.FIELD_MULTI_SELECT;
					colorField.m_bRequired = true;
				}
			}

			if (sizeField) {
				this.sizesLabel = sizeField.getControlName();
			}

			if (colorField) {
				this.colorsLabel = colorField.getControlName();
			}

			// add fields that may be needed when creating new Samples - not for Copy or Edit use cases
			if (!this.getViewNode()) {
				this.insertField(lang.mixin(new Field(), {
					bForceEditable: true,
					m_id: this.LINE_ITEM,
					m_typeControl: def.FIELD_MULTI_SELECT,
					widgetParams: {defaultAll: true},
					m_onChange: "_onChangeLineItem"
				}), 0);

				if (this.formInfo) {
					const {contextUrl} = this.formInfo;
					const getProduct = (url) => cacheView.get(cacheView.get(url)?.SRLineItemProduct);
					const lineItem = cacheView.get(contextUrl)?.LineItems?.find(getProduct);

					if (lineItem && parentNode.LineItems.length === 1) {
						this.urlProduct = getProduct(lineItem).$URL;
						this.addDefaultValue(this.LINE_ITEM, lineItem);
					}
				}


				let f = this.insertField(lang.mixin(new Field(), {
					m_bRequired: true,
					m_label: this.isSupplierFactory() ? FACTORY : SUPPLIER,
					m_id: "ProductSources",
					m_path: xml.path(xml.ATTR_PARENT, xml.ATTR_PARENT),
					omitFromReport: true,
					m_onChange: "_onChangeProductSources"
				}), 0);

				if (this.isAllocationWIP && !this.bSupplierRequest) {
					f.m_typeControl = def.FIELD_FILTERING_SELECT;
					f.m_type = xml.ATTR_REF;
				}

				if (!this.actionOnSizeChart()) {
					// set supplier / supplier factory dropdown to None by default
					const productSourcing = cacheView.getNode(parentNode.ProductSourcing);
					const designatedProductSource = cacheView.getNode(productSourcing?.DesignatedProductSource);
					fd.setFieldInitialValue(SUPPLIER, designatedProductSource ? [designatedProductSource.Supplier] : []);
					fd.setFieldInitialValue(FACTORY, designatedProductSource ? [designatedProductSource.SupplierFactory] : []);
				}


				var a = this._dimensionFields = [];

				if (sizeField) {
					a.push(sizeField);
				}

				if (colorField) {
					a.push(colorField);
				}

				a.push(this.insertField(lang.mixin(new Field(), {
					m_label: this.SIZE_RANGE,
					m_onChange: "_onChangeSizeRange",
					m_typeControl: def.FIELD_FILTERING_SELECT
				}), this.colorsLabel));

				this.insertField(lang.mixin(new Field(), {
					m_label: this.DIMENSIONS,
					m_onChange: "_onChangeDimensions",
					m_typeControl: def.FIELD_FILTERING_SELECT
				}), this.colorsLabel);

				const isInProductSource = this.getSourceController().getAction().classController === "Apparel.NewProductSourceSamples";
				if (this.isAllocationWIP && fd && !this.bSupplierRequest && !isInProductSource) {
					this.setFieldDisabled(this.DIMENSIONS, true);
				}

				a.push(this.insertField(lang.mixin(new Field(), {
					m_label: this.CUSTOM_QUANTITY,
					m_typeControl: def.FIELD_CHECKBOX,
					m_onChange: "_onChangeCustomQuantity",
					m_onValidate: "_onValidateCustomQuantity",
					bCombineWithPrevious: true,
					bHideLabelForCombinedField: false
				}), this.colorsLabel));

				a.push(this.insertField(lang.mixin(new Field(), {
					m_label: this.QUANTITY,
					m_type: xml.ATTR_INTEGER,
					widgetParams: {constraints: {min: 1, max: api.getSamplesQuantityMax()}}
				})));
			}

			if (this.isCopy()) {
				this.insertField(lang.mixin(new Field(), {
					m_label: this.COPY_SAMPLE_BOM,
					m_typeControl: def.FIELD_CHECKBOX
				}));

				// Clears Sample Field when copying a Sample
				fd.setFieldInitialValue(xml.ATTR_NAME, "");
			}
			this.addDefaultValue(this.QUANTITY, 1);

			this.inherited(arguments);
		},
		_buildView: function() {
			if (this.isCopy()) {
				var sample = this.getViewNode();
				var sampleDataSheets = this.cacheView.getNodes(sample.SampleDataSheets);
				var bSampleBom = _.findWhere(sampleDataSheets, {"$Type": "ApparelBOM"});

				if (bSampleBom) {
					this.addDefaultValue(this.COPY_SAMPLE_BOM, true);
				} else {
					this.addFixedValue(this.COPY_SAMPLE_BOM, false);
				}
			}

			this.inherited(arguments);
		},

		_setProductDimensions: function(/*String[]*/colors, /*String[]*/sizes, /*String?*/urlBS, /*Boolean*/bNone) {
			// summary: update form with new set of colors and sizes
			var fDim = this.defView.getField(this.DIMENSIONS);
			var nColors = colors ? colors.length : 0;
			var nSizes = sizes ? sizes.length : 0;
			const sizeFieldExists = !!this.defView.getField(this.SIZES);
			const colorFieldExists = !!this.defView.getField(this.COLORS);

			this._colors = this.productColor ? [this.productColor] : colors;
			this._sizes = sizes;
			this._urlBaseSize = xml.isValidURL(urlBS) ? urlBS : null;

			let isOneSourceSelected;
			if (this.bSupplierRequest) {
				const sources = this.defView.getFieldValue(this.isSupplierFactory() ? this.FACTORY : this.SUPPLIER)
				isOneSourceSelected = sources?.length === 1;
			}

			const isInProductSource = this.getSourceController().getAction().classController === "Apparel.NewProductSourceSamples";
			const productNode =  this.cacheView.getNode(this.urlProduct);
			this.isAllocationWIP = Sourcing.isProductColorwayAllocationWIP(productNode);
			if (isInProductSource && this.isAllocationWIP) {
				const productSource = this.getSourceView().getViewNode();
				const colorSourcesUrls = productSource.ColorSources;
				if (!colorSourcesUrls.length) {
					nColors = 0;
				} else {
					nColors = Sourcing.getCountColorsInActiveCPSNoLineItem(productNode, this.urlSource, this.cacheView);
				}
			}

			if (!this._defaultSizes) {
				this._defaultSizes = sizes;
				this._defaultBaseSize = this._urlBaseSize ? urlBS : null;
			}

			// nothing more to do if no field to show dimensions
			if (!fDim) {
				return null;
			}

			// populate possible drop-down values for dimensions
			var items = [];

			if (bNone && !this.productColor) {
				items.push({name: locale.resource("None"), id: this.DIM_NONE});
			}

			if (this._urlBaseSize && !this.productColor) {
				items.push({name: locale.resource("RequestSampleDimBaseSize"), id: this.DIM_BASE_SIZE});
			}

			// bNone is disallowed on SizeChartReview and there Colors option allow is disallowed too
			// in SR, if more than one supplier/factory is selected, only add colors to options if the only one product selected is not Allocation WIP
			//	if only one supplier/factory is selected, add colors to options if there is one product
			if (colorFieldExists &&
				 ((!this.bSupplierRequest && nColors && bNone) || 
				 (this.bSupplierRequest && nColors && bNone && !isOneSourceSelected && !this.isAllocationWIP) ||
				 (this.bSupplierRequest && nColors && bNone && isOneSourceSelected))) {
				items.push({name: locale.resource("RequestSampleDimColors"), id: this.DIM_COLORS});
			}

			// only need to show multiple sizes if more than one or base size is not selected
			if (sizeFieldExists && nSizes && (!this._urlBaseSize || (nSizes > 1)) && !this.productColor) {
				items.push({name: locale.resource("RequestSampleDimSizes"), id: this.DIM_SIZES});
			}

			if (colorFieldExists && sizeFieldExists && 
				((!this.bSupplierRequest && nColors && nSizes) || 
				(this.bSupplierRequest && nColors && nSizes && !isOneSourceSelected && !this.isAllocationWIP) || 
				(this.bSupplierRequest && nColors && nSizes && isOneSourceSelected))) {
				items.push({name: locale.resource("RequestSampleDimAll"), id: this.DIM_COLORS_AND_SIZES});
			}

			// sample per active color doesn't apply to Shape or inside of ColorMaterial
			// bNone is disallowed on SizeChartReview and there Colors option allow is disallowed too, so disable Sample per active Color
			var product = this.cacheView.getNode(this.urlProduct);
			let isColorMaterial = this.getSourceController()?.getParent()?.$Type === "ColorMaterial";
			if (product || !this.urlProduct) {
				if (!product || (product.$Type != "Shape" && !isColorMaterial && nColors && bNone)) {
					items.push({name: locale.resource("RequestSampleDimActiveColor"), id: this.DIM_PER_ACTIVE_COLOR});
				}
			}

			this._dimensionStore = new Memory({data: items});

			return _.map(items, function(obj) {return obj.id;});
		},

		_setProductType: function(/*String*/type) {
			// summary: determine sample type based on product type
			if (!this._typeSample) {
				if (type == "Style" || type == "Shape") {
					this._typeSample = "Sample";
				} else {
					this._typeSample = "MaterialSample";
				}

				var fSampleType = this.defView.getField("SampleType");
				if (fSampleType) {
					fSampleType.m_rca = reflection.getClass(this._typeSample).getAttribute(fSampleType.m_id);
				}
			}
		},

		_toWriterProductSamples: function(/*Controller*/ac, /*String*/urlProduct, /*String*/urlSource, /*String?*/urlLineItem) {
			// summary: create samples of selected color/sizes
			var w = ac.m_writer;
			var fd = this.defView.m_formData;
			var type = this._typeSample;
			var field = fd.getField(this.FACTORY) || fd.getField(this.SUPPLIER);
			var sourceName = field ? field.getNameForValue(urlSource) : this.sourceName;
			let prefix;
			let factoryURL;
			let factoryNode;
			const cacheView = this.cacheView;
			if (fd.getField(this.FACTORY) != null) {
				sourceNode = cacheView.getNode(urlSource);
				factoryURL = sourceNode.Factory;
				factoryNode = cacheView.getNode(factoryURL);
				sourceName = factoryNode.$Name;
			}
			prefix = fd.getFieldValue(xml.ATTR_NAME) == "" ? sourceName : fd.getFieldValue(xml.ATTR_NAME, true) + " - " + sourceName;
			var qty = fd.getFieldValue(this.QUANTITY, true) || 1; // default of 1
			var urlStorage = fd.getFieldValue(this.SAMPLE_STORAGE, true);
			var samples = [];

			// batch size
			var config = reflection.getObject("centric://REFLECTION/INSTANCE/SourcingModule/Configuration");
			var batchSize = config.SampleCreationBatchSize || 20; // 20 is default

			// use view to get field value in case field is hidden
			var dims = this.defView.getFieldValue(this.DIMENSIONS) || this.DIM_NONE; // default of NONE
			var nameField = this.defView.getField("Node Name");
			var nameHasExpr = (nameField && nameField.hasExpression());
			var self = this;

			var createSamples = function(n, colorURL, sizeURL, urlProductLocal) {
				for (var i = 1; i <= n; ++i) {
					// if there is a urlLineItem, make sure we have a matching urlLineItemLocal if there is one
					if (!urlLineItem || !urlProductLocal || cache.getNode(urlLineItem).SRLineItemProduct == urlProductLocal) {
						var url = comm.createUrl(type);

						var cn = w.createNode(url, type, name);

						ac.toWriterFields(url, fd);

						// see if there is an expression on Node Name set by the Business Object - if so, ignore this next part
						if (nameField && !nameHasExpr) {
							//Found that any string in double quotes were resulting in error while evaluating expression.Added this unicode replacement to fix that.
							var exp = "\"" + prefix.replace(/\"/g, "\\u0022") + "\"";

							// populate color / size attributes
							if (colorURL) {
								exp += "+ \"-\" + ProductColor.`Node Name`";
							}

							if (sizeURL) {
								exp += "+ \"-\" + ProductSize.`Node Name`";
							}

							if (n > 1) {
								exp += "+ \"-" + i + "\"";
							}

							// use one-time expression for Name
							cn.changeAttributeExp("Node Name", exp, true); // one time expr
						}

						if (urlLineItem) {
							cn.changeAttribute("SampleSRLineItem", xml.ATTR_REF, urlLineItem);
						}

						if (sizeURL) {
							cn.changeAttribute("ProductSize", xml.ATTR_REF, sizeURL);
						}

						if (colorURL) {
							// call server method to update color since this is bi-drectional relationship and
							// creating a sample should not require edit permission on color
							w.callMethod("Apparel", "ProductSample_UpdateProductColor")
								.addParameter(xml.PROP_URL, xml.ATTR_REF, url)
								.addParameter(xml.PROP_VALUE, xml.ATTR_REF, colorURL);
						}

						w.callMethod("Apparel", "ProductAddSamples")
							.addParameter("ProductURL", xml.ATTR_REF, urlProduct)
							.addParameter("SourceURL", xml.ATTR_REF, urlSource)
							.addParameter("SampleStorageURL", xml.ATTR_REF, urlStorage)
							.addParameter("SampleURLs", xml.ATTR_REFLIST, [url])
							.addParameter("DefaultDimensions", xml.ATTR_BOOLEAN, !dims)
							.addParameter("ParentURL", xml.ATTR_REF, ac.getParentURL());

						// if only one sample created, then use name given rather than name constructed from color, size and qty
						if (n == 1) {
							w.changeNode(url).changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, prefix);
						}

						w.validateNode(url, true);

						self.batchSampleCtr++;
						if (self.batchSampleCtr == batchSize) {
							w.flushChanges();
							self.batchSampleCtr = 0;
						}

						samples.push(url);
					}
				}
			};

			var matrix = this._matrix;
			if (matrix) {
				var bColorsAndSizes = (dims == this.DIM_COLORS_AND_SIZES);
				var bColors = (dims == this.DIM_COLORS);
				var sizeResults = bColorsAndSizes ? matrix.columnStore.query() : null;

				// update data from editor fields.
				var matrixValues = matrix.get("value");

				matrix.rowStore.query().forEach(function(item, i) {
					var id = item.id;
					var values = matrixValues[i];

					if (sizeResults) {
						sizeResults.forEach(function(size, i) {
							createSamples(values[i], id, size.id);
						});
					} else {
						createSamples(values[0], bColors ? id : null, bColors ? null : id);
					}
				}, this);
			} else {
				var quantity = Number(qty);
				var colors = this.defView.m_formData.getFieldValue(this.colorsLabel, true) || [];
				var sizes = this.defView.m_formData.getFieldValue(this.sizesLabel, true) || [];

				if (dims == this.DIM_NONE) {
					createSamples(quantity, null, null);
				} else if (dims == this.DIM_BASE_SIZE) {
					createSamples(quantity, null, this._urlBaseSize);
				} else if (dims == this.DIM_COLORS) {
					// loop through Colors
					colors.forEach(function(colorURL) {
						createSamples(quantity, colorURL, null);
					});
				} else if (dims == this.DIM_SIZES) {
					// loop through Sizes
					sizes.forEach(function(sizeURL) {
						createSamples(quantity, null, sizeURL);
					});
				} else if (dims == this.DIM_COLORS_AND_SIZES) {
					// loop through both Sizes / Colors
					colors.forEach(function(colorURL) {
						sizes.forEach(function(sizeURL) { // cartesian product
							createSamples(quantity, colorURL, sizeURL);
						});
					});
				} else if (dims == this.DIM_PER_ACTIVE_COLOR) {
					// for each product create samples for the active colors
					let cache = this.cacheView;
					// if multiple suppliers and products are selected, use this_productColors as this_colors might be null
					let pc = this._colors.length ? this._colors : this.removeDupesAndMapToNode([...Object.values(this._productColors)]);
					let {isAllocationWIP, bSupplierRequest} = this;
					const isSupplierFactory = this.isSupplierFactory();
					pc.forEach(function(p) {
						if (p.Active) {
							const product = cacheView.getNode(urlProduct);
							isAllocationWIP = Sourcing.isProductColorwayAllocationWIP(product);
							if (isAllocationWIP && bSupplierRequest) {
								// if the product has ColorwayAllocation === WIP, only create Sample if color is in ColorProductSource.Colorway
								const isColorInColorSources = Sourcing.isColorInColorSources(urlLineItem, p.$URL, urlSource, isSupplierFactory, cache);
								if (isColorInColorSources) {
									createSamples(quantity, p.$URL);
								} else {
									return;
								}
							} else if (isAllocationWIP && !xml.isValidURL(urlLineItem)) {
								const isColorInColorSources = Sourcing.isColorInColorSourcesNoLineItem(product, p.$URL, urlSource, isSupplierFactory, cache);
								if (isColorInColorSources) {
									createSamples(quantity, p.$URL);
								} else {
									return;
								}
							} else {
								if (product.ProductColors.includes(p.$URL)) {
									createSamples(quantity, p.$URL);
								}
							}
						}
					});
				}
			}

			return samples;
		},

		/**
		 * Get list of CNL Strings and return a list of nodes with those URLs
		 * @param {String[]} data 
		 * @returns 
		 */
		removeDupesAndMapToNode(data) {
			data = data.flat();
			let dataNoDupes = [...new Set(data)];
			let result = dataNoDupes.map((d) => this.cacheView.getNode(d));
			return result;
		},

		/**
		 * @override
		 * hide "factories/suppliers selected" on side panel (Sample Overview) depending if FDD is enabled or not.
		 */
		_buildView: function() {
			this.inherited(arguments);
			// If its a Copy/Edit Sample we dont want to hide these since copy/edit don't use same widget
			if (!this.isCopy() && !this.isEdit()) {
				if (this.isSupplierFactory()) {
					html.hide(this.rightWidget.suppliersNode.parentNode);
				} else {
					html.hide(this.rightWidget.factoriesNode.parentNode);
				}
				if (this.cacheView.getNode(this.urlProduct)?.$Type === "Material" || this.getSourceController().getActionNodeType() === "MaterialSample") {
					html.hide(this.rightWidget.colorwaysNode.parentNode);
				} else {
					html.hide(this.rightWidget.coloredMaterialsNode.parentNode);
				}
			}
		},

		isSupplierFactory() {
			if (this.actionOnSizeChart()) {
				const isFDDEnabled = ProductType.isFDDProduct(this.parentNode);
				return isFDDEnabled;
			}

			let productSourceCreationLevel;
			let fd = this.defView.m_formData;
			const cache = this.getCache();
			const parent = fd.m_source.m_urlParent;
			const parentNode = cache.getNode(parent);

			// this.urlProduct is null when sample form is used inside supplier request
			if (parentNode?.$Type == "SupplierRequest" && this.urlProduct) {
				productSourceCreationLevel = Sourcing.getProductSourceCreationLevel(this.urlProduct, cache);
			} else if (parentNode?.$Type == "ProductSource") {
				return xml.isValidURL(parentNode.SupplierFactory);
			} else if (parentNode?.$Type == "ColorMaterial") { // if in ColorMaterial, pass in the Url of the material we came from
				productSourceCreationLevel = Sourcing.getProductSourceCreationLevel(this.getSourceContext().getParentURL(), cache);
			} else {
				productSourceCreationLevel = Sourcing.getProductSourceCreationLevel(parentNode.$URL, cache);
			}

			return productSourceCreationLevel == "ProductSourceCreationLevel:Factory";
		},

		/**
		 * Necesary Check if we are doing this action on sizeChart since FDD hasnt been enabled yet
		 */
		actionOnSizeChart() {
			return routerStore.getBreadcrumbNode("SizeChart") != null;
		}
	});
});